import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Tabs, Row, Col, TimePicker, InputNumber, Button,
  message, Alert, Card, Typography, Select, Table, Tag, Popconfirm,
  Space, Divider, Checkbox, Radio, DatePicker, theme
} from 'antd';
import {
  ClockCircleOutlined, SaveOutlined, UserOutlined, TeamOutlined,
  ThunderboltOutlined, DeleteOutlined, CalendarOutlined,
  AppstoreOutlined, EditOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Option } = Select;
const { Text } = Typography;

const DAY_OPTIONS = [
  { label: 'Monday', value: 'MON' },
  { label: 'Tuesday', value: 'TUE' },
  { label: 'Wednesday', value: 'WED' },
  { label: 'Thursday', value: 'THU' },
  { label: 'Friday', value: 'FRI' },
  { label: 'Saturday', value: 'SAT' },
  { label: 'Sunday', value: 'SUN' }
];

export default function ShiftSettingsModal({
  open,
  onCancel,
  shiftSettings = [],
  availableCombinations = [],
  staffList = [],
  candidatesList = [],
  isStudio = false,
  onSaveShiftSettings,
  onDeleteShiftSetting,
  saving = false
}) {
  const { token } = theme.useToken();
  const [activePersonType, setActivePersonType] = useState('CANDIDATE');

  // Candidate Form States
  const [candScheduleType, setCandScheduleType] = useState('DAYS'); // 'DAYS' or 'DATE'
  const [candSpecificDate, setCandSpecificDate] = useState(dayjs());
  const [selectedCandDays, setSelectedCandDays] = useState(['MON', 'TUE', 'WED', 'THU', 'FRI']);
  const [candCombination, setCandCombination] = useState('ALL'); // 'ALL' or combination string
  const [candCandidateId, setCandCandidateId] = useState('ALL'); // 'ALL' or specific candidate_id
  const [editingCandSettingId, setEditingCandSettingId] = useState(null);

  // Staff Form States
  const [staffTargetType, setStaffTargetType] = useState('ALL'); // 'ALL' or 'INDIVIDUAL'
  const [selectedStaffId, setSelectedStaffId] = useState(undefined);
  const [staffScheduleType, setStaffScheduleType] = useState('DAYS'); // 'DAYS' or 'DATE'
  const [staffSpecificDate, setStaffSpecificDate] = useState(dayjs());
  const [selectedStaffDays, setSelectedStaffDays] = useState(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']);
  const [editingStaffSettingId, setEditingStaffSettingId] = useState(null);

  const [staffForm] = Form.useForm();
  const [candidateForm] = Form.useForm();

  const parseTime = (timeStr, fallback = '09:00:00') => {
    const s = timeStr || fallback;
    return dayjs(`2026-01-01 ${s}`);
  };

  const formatTime = (timeObj) => {
    return timeObj ? timeObj.format('HH:mm:ss') : '09:00:00';
  };

  // Convert day string to array of short days
  const parseDayTypeToDays = (dt) => {
    const s = String(dt || 'ALL').trim().toUpperCase();
    if (s === 'ALL') return ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
    if (s === 'WEEKDAY') return ['MON', 'TUE', 'WED', 'THU', 'FRI'];
    if (s === 'SATURDAY' || s === 'SAT') return ['SAT'];
    if (s === 'SUNDAY' || s === 'SUN') return ['SUN'];
    return s.split(',').map(d => d.trim().substring(0, 3)).filter(d => DAY_OPTIONS.some(opt => opt.value === d));
  };

  // Convert array of selected days to stored day_type string
  const formatDaysToDayType = (days) => {
    if (!days || !days.length) return 'ALL';
    if (days.length === 7) return 'ALL';
    const sorted = DAY_OPTIONS.filter(d => days.includes(d.value)).map(d => d.value);
    const joined = sorted.join(',');
    if (joined === 'MON,TUE,WED,THU,FRI') return 'WEEKDAY';
    if (joined === 'SAT') return 'SATURDAY';
    return joined;
  };

  // Reset/Populate Candidate form when selection mode or edit mode changes
  useEffect(() => {
    if (!open) return;

    if (editingCandSettingId) {
      const match = (shiftSettings || []).find(s => s.setting_id === editingCandSettingId);
      if (match) {
        candidateForm.setFieldsValue({
          login_from: parseTime(match.login_from, '09:00:00'),
          login_to: parseTime(match.login_to, '09:30:00'),
          logout_from: parseTime(match.logout_from, '16:30:00'),
          logout_to: parseTime(match.logout_to, '17:30:00'),
          full_day_hours: parseFloat(match.full_day_hours || 7.0),
          half_day_hours: parseFloat(match.half_day_hours || 3.5),
          overtime_threshold_hours: parseFloat(match.overtime_threshold_hours || match.full_day_hours || 7.0)
        });
        return;
      }
    }

    if (candScheduleType === 'DATE') {
      const dateStr = candSpecificDate ? candSpecificDate.format('YYYY-MM-DD') : null;
      const match = (shiftSettings || []).find(s =>
        s.person_type === 'CANDIDATE' &&
        s.schedule_type === 'DATE' &&
        s.specific_date === dateStr &&
        s.combination === candCombination &&
        (candCandidateId === 'ALL' ? (!s.candidate_id) : Number(s.candidate_id) === Number(candCandidateId))
      );

      candidateForm.setFieldsValue({
        login_from: parseTime(match?.login_from, '09:00:00'),
        login_to: parseTime(match?.login_to, '09:30:00'),
        logout_from: parseTime(match?.logout_from, '16:30:00'),
        logout_to: parseTime(match?.logout_to, '17:30:00'),
        full_day_hours: parseFloat(match?.full_day_hours || 7.0),
        half_day_hours: parseFloat(match?.half_day_hours || 3.5),
        overtime_threshold_hours: parseFloat(match?.overtime_threshold_hours || match?.full_day_hours || 7.0)
      });
    } else {
      const currentDayTypeStr = formatDaysToDayType(selectedCandDays);
      const isOnlySaturday = selectedCandDays.length === 1 && selectedCandDays[0] === 'SAT';

      const match = (shiftSettings || []).find(s =>
        s.person_type === 'CANDIDATE' &&
        s.schedule_type !== 'DATE' &&
        s.day_type === currentDayTypeStr &&
        s.combination === candCombination
      ) || (shiftSettings || []).find(s =>
        s.person_type === 'CANDIDATE' &&
        s.schedule_type !== 'DATE' &&
        s.day_type === currentDayTypeStr &&
        (s.combination === 'ALL' || !s.combination)
      ) || (shiftSettings || []).find(s =>
        s.person_type === 'CANDIDATE' &&
        s.schedule_type !== 'DATE' &&
        (s.day_type === 'ALL' || !s.day_type) &&
        s.combination === candCombination
      );

      candidateForm.setFieldsValue({
        login_from: parseTime(match?.login_from, '09:00:00'),
        login_to: parseTime(match?.login_to, '09:30:00'),
        logout_from: parseTime(match?.logout_from, isOnlySaturday ? '13:30:00' : '16:30:00'),
        logout_to: parseTime(match?.logout_to, isOnlySaturday ? '14:30:00' : '17:30:00'),
        full_day_hours: parseFloat(match?.full_day_hours || (isOnlySaturday ? 4.0 : 7.0)),
        half_day_hours: parseFloat(match?.half_day_hours || (isOnlySaturday ? 2.0 : 3.5)),
        overtime_threshold_hours: parseFloat(match?.overtime_threshold_hours || match?.full_day_hours || (isOnlySaturday ? 4.0 : 7.0))
      });
    }
  }, [open, candScheduleType, candSpecificDate, selectedCandDays, candCombination, candCandidateId, editingCandSettingId, shiftSettings, candidateForm]);

  // Reset/Populate Staff form when selection mode or edit mode changes
  useEffect(() => {
    if (!open || isStudio) return;

    if (editingStaffSettingId) {
      const match = (shiftSettings || []).find(s => s.setting_id === editingStaffSettingId);
      if (match) {
        staffForm.setFieldsValue({
          login_from: parseTime(match.login_from, '09:00:00'),
          login_to: parseTime(match.login_to, '09:30:00'),
          logout_from: parseTime(match.logout_from, '17:30:00'),
          logout_to: parseTime(match.logout_to, '18:30:00'),
          full_day_hours: parseFloat(match.full_day_hours || 8.0),
          half_day_hours: parseFloat(match.half_day_hours || 4.0),
          overtime_threshold_hours: parseFloat(match.overtime_threshold_hours || match.full_day_hours || 8.0)
        });
        return;
      }
    }

    const sId = staffTargetType === 'INDIVIDUAL' && selectedStaffId ? Number(selectedStaffId) : null;

    if (staffScheduleType === 'DATE') {
      const dateStr = staffSpecificDate ? staffSpecificDate.format('YYYY-MM-DD') : null;
      const match = (shiftSettings || []).find(s =>
        s.person_type === 'STAFF' &&
        s.schedule_type === 'DATE' &&
        s.specific_date === dateStr &&
        (sId ? Number(s.staff_id) === sId : !s.staff_id)
      );

      staffForm.setFieldsValue({
        login_from: parseTime(match?.login_from, '09:00:00'),
        login_to: parseTime(match?.login_to, '09:30:00'),
        logout_from: parseTime(match?.logout_from, '17:30:00'),
        logout_to: parseTime(match?.logout_to, '18:30:00'),
        full_day_hours: parseFloat(match?.full_day_hours || 8.0),
        half_day_hours: parseFloat(match?.half_day_hours || 4.0),
        overtime_threshold_hours: parseFloat(match?.overtime_threshold_hours || match?.full_day_hours || 8.0)
      });
    } else {
      const currentDayTypeStr = formatDaysToDayType(selectedStaffDays);
      const match = (shiftSettings || []).find(s =>
        s.person_type === 'STAFF' &&
        s.schedule_type !== 'DATE' &&
        s.day_type === currentDayTypeStr &&
        (sId ? Number(s.staff_id) === sId : !s.staff_id)
      ) || (shiftSettings || []).find(s =>
        s.person_type === 'STAFF' &&
        s.schedule_type !== 'DATE' &&
        (s.day_type === 'ALL' || !s.day_type) &&
        (sId ? Number(s.staff_id) === sId : !s.staff_id)
      ) || (shiftSettings || []).find(s =>
        s.person_type === 'STAFF' &&
        s.schedule_type !== 'DATE' &&
        !s.staff_id
      );

      staffForm.setFieldsValue({
        login_from: parseTime(match?.login_from, '09:00:00'),
        login_to: parseTime(match?.login_to, '09:30:00'),
        logout_from: parseTime(match?.logout_from, '17:30:00'),
        logout_to: parseTime(match?.logout_to, '18:30:00'),
        full_day_hours: parseFloat(match?.full_day_hours || 8.0),
        half_day_hours: parseFloat(match?.half_day_hours || 4.0),
        overtime_threshold_hours: parseFloat(match?.overtime_threshold_hours || match?.full_day_hours || 8.0)
      });
    }
  }, [open, isStudio, staffTargetType, selectedStaffId, staffScheduleType, staffSpecificDate, selectedStaffDays, editingStaffSettingId, shiftSettings, staffForm]);

  // Apply quick presets for candidate days
  const handleApplyCandPreset = (preset) => {
    if (preset === 'WEEKDAY') setSelectedCandDays(['MON', 'TUE', 'WED', 'THU', 'FRI']);
    else if (preset === 'SATURDAY') setSelectedCandDays(['SAT']);
    else if (preset === 'SUNDAY') setSelectedCandDays(['SUN']);
    else if (preset === 'ALL') setSelectedCandDays(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']);
  };

  // Apply quick presets for staff days
  const handleApplyStaffPreset = (preset) => {
    if (preset === 'WEEKDAY') setSelectedStaffDays(['MON', 'TUE', 'WED', 'THU', 'FRI']);
    else if (preset === 'MON_SAT') setSelectedStaffDays(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']);
    else if (preset === 'SATURDAY') setSelectedStaffDays(['SAT']);
    else if (preset === 'ALL') setSelectedStaffDays(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']);
  };

  const handleSave = async (personType) => {
    try {
      const isCand = personType === 'CANDIDATE';
      const form = isCand ? candidateForm : staffForm;
      const values = await form.validateFields();

      let payload = {
        setting_id: isCand ? editingCandSettingId : editingStaffSettingId,
        person_type: personType,
        login_from: formatTime(values.login_from),
        login_to: formatTime(values.login_to),
        logout_from: formatTime(values.logout_from),
        logout_to: formatTime(values.logout_to),
        full_day_hours: values.full_day_hours,
        half_day_hours: values.half_day_hours,
        overtime_threshold_hours: values.overtime_threshold_hours
      };

      if (isCand) {
        payload.schedule_type = candScheduleType;
        if (candScheduleType === 'DATE') {
          if (!candSpecificDate) {
            message.warning('Please select a specific date');
            return;
          }
          payload.specific_date = candSpecificDate.format('YYYY-MM-DD');
          payload.day_type = 'DATE_OVERRIDE';
        } else {
          if (!selectedCandDays || !selectedCandDays.length) {
            message.warning('Please select at least one day for the schedule');
            return;
          }
          payload.specific_date = null;
          payload.day_type = formatDaysToDayType(selectedCandDays);
        }
        payload.combination = candCombination;
        payload.candidate_id = candCandidateId !== 'ALL' ? Number(candCandidateId) : null;
      } else {
        // STAFF
        if (staffTargetType === 'INDIVIDUAL' && !selectedStaffId) {
          message.warning('Please select a specific staff member');
          return;
        }

        payload.schedule_type = staffScheduleType;
        payload.combination = 'ALL';
        payload.staff_id = staffTargetType === 'INDIVIDUAL' && selectedStaffId ? Number(selectedStaffId) : null;

        if (staffScheduleType === 'DATE') {
          if (!staffSpecificDate) {
            message.warning('Please select a specific date');
            return;
          }
          payload.specific_date = staffSpecificDate.format('YYYY-MM-DD');
          payload.day_type = 'DATE_OVERRIDE';
        } else {
          if (!selectedStaffDays || !selectedStaffDays.length) {
            message.warning('Please select at least one day for the schedule');
            return;
          }
          payload.specific_date = null;
          payload.day_type = formatDaysToDayType(selectedStaffDays);
        }
      }

      await onSaveShiftSettings(payload);
      message.success(`${personType === 'STAFF' ? 'Staff' : 'Candidate'} shift timings saved successfully!`);

      // Reset edit state
      if (isCand) {
        setEditingCandSettingId(null);
      } else {
        setEditingStaffSettingId(null);
      }
    } catch (err) {
      if (err.errorFields) return; // Antd validation error
      message.error(err.message || 'Failed to save shift timings');
    }
  };

  const renderDayBadges = (record) => {
    if (record.schedule_type === 'DATE' || record.specific_date) {
      const dateFormatted = record.specific_date ? dayjs(record.specific_date).format('DD/MM/YYYY (ddd)') : 'Date Specific';
      return <Tag color="magenta" icon={<CalendarOutlined />}>Date: {dateFormatted}</Tag>;
    }

    const days = parseDayTypeToDays(record.day_type);
    if (days.length === 7) return <Tag color="blue">All Days (Mon - Sun)</Tag>;
    if (days.length === 6 && !days.includes('SUN')) return <Tag color="geekblue">Mon - Sat (6 Days)</Tag>;
    if (days.length === 5 && !days.includes('SAT') && !days.includes('SUN')) return <Tag color="cyan">Mon - Fri (Weekdays)</Tag>;
    if (days.length === 1 && days[0] === 'SAT') return <Tag color="purple">Saturday Only</Tag>;
    if (days.length === 1 && days[0] === 'SUN') return <Tag color="volcano">Sunday Only</Tag>;

    return (
      <Space size={[2, 2]} wrap>
        {days.map(d => (
          <Tag key={d} color="geekblue" style={{ fontSize: 11, padding: '0 4px', margin: 1 }}>
            {d}
          </Tag>
        ))}
      </Space>
    );
  };

  const candidateRules = (shiftSettings || []).filter(s => s.person_type === 'CANDIDATE');
  const staffRules = (shiftSettings || []).filter(s => s.person_type === 'STAFF');

  const candRuleColumns = [
    {
      title: 'Schedule / Date',
      key: 'schedule',
      width: 220,
      render: (_, r) => renderDayBadges(r)
    },
    {
      title: 'Combination / Candidate',
      key: 'target',
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          <Tag color={r.combination === 'ALL' || !r.combination ? 'default' : 'blue'}>
            Comb: {r.combination === 'ALL' || !r.combination ? 'All (Default)' : r.combination}
          </Tag>
          {r.candidate_id && (
            <Tag color="orange" icon={<UserOutlined />}>
              Candidate: {r.candidate_name || `ID: ${r.candidate_id}`}
            </Tag>
          )}
        </Space>
      )
    },
    {
      title: 'Login Window',
      key: 'login_window',
      render: (_, r) => (
        <span style={{ fontSize: 12 }}>
          {r.login_from?.substring(0, 5)} - <b>{r.login_to?.substring(0, 5)}</b>
        </span>
      )
    },
    {
      title: 'Logout Window',
      key: 'logout_window',
      render: (_, r) => (
        <span style={{ fontSize: 12 }}>
          {r.logout_from?.substring(0, 5)} - <b>{r.logout_to?.substring(0, 5)}</b>
        </span>
      )
    },
    {
      title: 'Hours (Full / Half / OT)',
      key: 'hours',
      render: (_, r) => (
        <span style={{ fontSize: 12 }}>
          <b style={{ color: token.colorSuccess }}>{parseFloat(r.full_day_hours || 0).toFixed(1)}h</b> / {parseFloat(r.half_day_hours || 0).toFixed(1)}h / {parseFloat(r.overtime_threshold_hours || r.full_day_hours || 0).toFixed(1)}h
        </span>
      )
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_, r) => (
        <Space size="small">
          <Button
            size="small"
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingCandSettingId(r.setting_id);
              if (r.schedule_type === 'DATE' || r.specific_date) {
                setCandScheduleType('DATE');
                setCandSpecificDate(r.specific_date ? dayjs(r.specific_date) : dayjs());
              } else {
                setCandScheduleType('DAYS');
                setSelectedCandDays(parseDayTypeToDays(r.day_type));
              }
              setCandCombination(r.combination || 'ALL');
              setCandCandidateId(r.candidate_id ? String(r.candidate_id) : 'ALL');

              candidateForm.setFieldsValue({
                login_from: r.login_from ? dayjs(r.login_from, 'HH:mm:ss') : null,
                login_to: r.login_to ? dayjs(r.login_to, 'HH:mm:ss') : null,
                logout_from: r.logout_from ? dayjs(r.logout_from, 'HH:mm:ss') : null,
                logout_to: r.logout_to ? dayjs(r.logout_to, 'HH:mm:ss') : null,
                full_day_hours: r.full_day_hours !== undefined ? parseFloat(r.full_day_hours) : 7.0,
                half_day_hours: r.half_day_hours !== undefined ? parseFloat(r.half_day_hours) : 3.5,
                overtime_threshold_hours: r.overtime_threshold_hours !== undefined ? parseFloat(r.overtime_threshold_hours) : (r.full_day_hours || 7.0)
              });
            }}
          >
            Edit
          </Button>
          {onDeleteShiftSetting && r.setting_id && !(r.day_type === 'WEEKDAY' && r.combination === 'ALL' && !r.schedule_type) && (
            <Popconfirm
              title="Delete this candidate shift timing rule?"
              onConfirm={() => onDeleteShiftSetting(r.setting_id)}
            >
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  const staffRuleColumns = [
    {
      title: 'Staff Target',
      key: 'target',
      width: 220,
      render: (_, r) => {
        if (r.staff_id) {
          const staffObj = staffList.find(s => Number(s.staff_id) === Number(r.staff_id));
          return (
            <Tag color="purple" icon={<UserOutlined />}>
              <b>{staffObj?.staff_name || r.staff_name || `Staff ID: ${r.staff_id}`}</b>
              {(staffObj?.staff_code || r.staff_code) ? ` (${staffObj?.staff_code || r.staff_code})` : ''}
            </Tag>
          );
        }
        return <Tag color="blue" icon={<TeamOutlined />}>All Staff Members (Global)</Tag>;
      }
    },
    {
      title: 'Schedule / Date',
      key: 'schedule',
      render: (_, r) => renderDayBadges(r)
    },
    {
      title: 'Login Window',
      key: 'login_window',
      render: (_, r) => (
        <span style={{ fontSize: 12 }}>
          {r.login_from?.substring(0, 5)} - <b>{r.login_to?.substring(0, 5)}</b>
        </span>
      )
    },
    {
      title: 'Logout Window',
      key: 'logout_window',
      render: (_, r) => (
        <span style={{ fontSize: 12 }}>
          {r.logout_from?.substring(0, 5)} - <b>{r.logout_to?.substring(0, 5)}</b>
        </span>
      )
    },
    {
      title: 'Hours (Full / Half / OT)',
      key: 'hours',
      render: (_, r) => (
        <span style={{ fontSize: 12 }}>
          <b style={{ color: token.colorSuccess }}>{parseFloat(r.full_day_hours || 0).toFixed(1)}h</b> / {parseFloat(r.half_day_hours || 0).toFixed(1)}h / {parseFloat(r.overtime_threshold_hours || r.full_day_hours || 0).toFixed(1)}h
        </span>
      )
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_, r) => (
        <Space size="small">
          <Button
            size="small"
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingStaffSettingId(r.setting_id);
              if (r.staff_id) {
                setStaffTargetType('INDIVIDUAL');
                setSelectedStaffId(Number(r.staff_id));
              } else {
                setStaffTargetType('ALL');
                setSelectedStaffId(undefined);
              }

              if (r.schedule_type === 'DATE' || r.specific_date) {
                setStaffScheduleType('DATE');
                setStaffSpecificDate(r.specific_date ? dayjs(r.specific_date) : dayjs());
              } else {
                setStaffScheduleType('DAYS');
                setSelectedStaffDays(parseDayTypeToDays(r.day_type));
              }

              staffForm.setFieldsValue({
                login_from: r.login_from ? dayjs(r.login_from, 'HH:mm:ss') : null,
                login_to: r.login_to ? dayjs(r.login_to, 'HH:mm:ss') : null,
                logout_from: r.logout_from ? dayjs(r.logout_from, 'HH:mm:ss') : null,
                logout_to: r.logout_to ? dayjs(r.logout_to, 'HH:mm:ss') : null,
                full_day_hours: r.full_day_hours !== undefined ? parseFloat(r.full_day_hours) : 8.0,
                half_day_hours: r.half_day_hours !== undefined ? parseFloat(r.half_day_hours) : 4.0,
                overtime_threshold_hours: r.overtime_threshold_hours !== undefined ? parseFloat(r.overtime_threshold_hours) : (r.full_day_hours || 8.0)
              });
            }}
          >
            Edit
          </Button>
          {onDeleteShiftSetting && r.setting_id && !(r.day_type === 'ALL' && !r.staff_id && !r.specific_date) && (
            <Popconfirm
              title="Delete this staff shift timing rule?"
              onConfirm={() => onDeleteShiftSetting(r.setting_id)}
            >
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  const tabItems = [
    {
      key: 'CANDIDATE',
      label: (
        <span>
          <TeamOutlined /> Candidate Shift Timings (Manual Days, Combination & Date-Wise)
        </span>
      ),
      children: (
        <div>
          {/* Header Controls for Candidate */}
          <Card
            size="small"
            style={{
              marginBottom: 16,
              background: token.colorSuccessBg || 'rgba(82, 196, 26, 0.06)',
              borderColor: token.colorSuccessBorder || 'rgba(82, 196, 26, 0.3)'
            }}
          >
            <Row gutter={[16, 12]} align="middle">
              <Col xs={24} md={10}>
                <div style={{ fontSize: 12, fontWeight: 'bold', color: token.colorSuccess, marginBottom: 6 }}>
                  <CalendarOutlined /> Schedule Configuration Mode:
                </div>
                <Radio.Group
                  value={candScheduleType}
                  onChange={(e) => {
                    setCandScheduleType(e.target.value);
                    setEditingCandSettingId(null);
                  }}
                  buttonStyle="solid"
                  size="small"
                >
                  <Radio.Button value="DAYS">📅 Regular Schedule (Day-Wise)</Radio.Button>
                  <Radio.Button value="DATE">🗓️ Date-Wise Override (Specific Date)</Radio.Button>
                </Radio.Group>
              </Col>

              <Col xs={24} md={8}>
                <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 6, color: token.colorPrimary }}>
                  <AppstoreOutlined /> Target Combination:
                </div>
                <Select
                  value={candCombination}
                  onChange={(v) => {
                    setCandCombination(v);
                    setEditingCandSettingId(null);
                  }}
                  style={{ width: '100%' }}
                  size="small"
                >
                  <Option value="ALL">All Combinations (Default)</Option>
                  {availableCombinations.map(comb => (
                    <Option key={comb} value={comb}>
                      {comb} (Custom Combination Timings)
                    </Option>
                  ))}
                </Select>
              </Col>

              <Col xs={24} md={6}>
                <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 6, color: token.colorWarning || '#fa8c16' }}>
                  <UserOutlined /> Candidate Scope:
                </div>
                <Select
                  value={candCandidateId}
                  onChange={(v) => {
                    setCandCandidateId(v);
                    setEditingCandSettingId(null);
                  }}
                  style={{ width: '100%' }}
                  size="small"
                  showSearch
                  optionFilterProp="children"
                >
                  <Option value="ALL">All Candidates in Combination</Option>
                  {candidatesList.map(c => (
                    <Option key={c.candidate_id} value={String(c.candidate_id)}>
                      {c.name} (ID: {c.candidate_id})
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>

            <Divider style={{ margin: '10px 0' }} />

            {/* Scope Details (Days vs Specific Date) */}
            {candScheduleType === 'DAYS' ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: '600', color: token.colorTextSecondary }}>
                    Select Days of the Week:
                  </div>
                  <Space size="small">
                    <Button size="small" onClick={() => handleApplyCandPreset('WEEKDAY')}>
                      Mon - Fri
                    </Button>
                    <Button size="small" onClick={() => handleApplyCandPreset('SATURDAY')}>
                      Saturday
                    </Button>
                    <Button size="small" onClick={() => handleApplyCandPreset('SUNDAY')}>
                      Sunday
                    </Button>
                    <Button size="small" onClick={() => handleApplyCandPreset('ALL')}>
                      All 7 Days
                    </Button>
                  </Space>
                </div>
                <Checkbox.Group
                  options={DAY_OPTIONS}
                  value={selectedCandDays}
                  onChange={(days) => {
                    setSelectedCandDays(days);
                    setEditingCandSettingId(null);
                  }}
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
                />
              </div>
            ) : (
              <Row gutter={12} align="middle">
                <Col xs={24} sm={12} md={8}>
                  <div style={{ fontSize: 12, fontWeight: '600', color: token.colorTextSecondary, marginBottom: 4 }}>
                    Select Specific Calendar Date:
                  </div>
                  <DatePicker
                    value={candSpecificDate}
                    onChange={(d) => {
                      setCandSpecificDate(d);
                      setEditingCandSettingId(null);
                    }}
                    format="DD/MM/YYYY"
                    style={{ width: '100%' }}
                    allowClear={false}
                  />
                </Col>
                <Col xs={24} sm={12} md={16}>
                  <Alert
                    message="Date-Wise Shift Override"
                    description="This specific date rule takes top priority over general regular days rules on the chosen date."
                    type="warning"
                    showIcon
                    style={{ padding: '4px 10px', fontSize: 12 }}
                  />
                </Col>
              </Row>
            )}
          </Card>

          {/* Form */}
          <Form form={candidateForm} layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Card size="small" title={<span style={{ color: token.colorPrimary }}><ClockCircleOutlined /> Check-In / Login Window</span>} style={{ marginBottom: 16 }}>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item name="login_from" label="Login From (Shift Start)" rules={[{ required: true }]}>
                        <TimePicker format="HH:mm:ss" style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="login_to" label="Login To (Late Cutoff)" rules={[{ required: true }]} tooltip="Punches after this time will be marked as Late (L)">
                        <TimePicker format="HH:mm:ss" style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Col>

              <Col span={12}>
                <Card size="small" title={<span style={{ color: token.colorPurple || '#9333ea' }}><ClockCircleOutlined /> Check-Out / Logout Window</span>} style={{ marginBottom: 16 }}>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item name="logout_from" label="Logout From (Early Leave Cutoff)" rules={[{ required: true }]} tooltip="Check-out before this time is marked as Early Leave (LE)">
                        <TimePicker format="HH:mm:ss" style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="logout_to" label="Logout To (Standard Shift End)" rules={[{ required: true }]} tooltip="Punches after this time qualify for Overtime (OT)">
                        <TimePicker format="HH:mm:ss" style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>

            <Card size="small" title={<span style={{ color: token.colorSuccess }}><ThunderboltOutlined /> Duration & Threshold Hours</span>} style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="full_day_hours" label="Full Day Standard Hours (P)" rules={[{ required: true }]} tooltip="Minimum hours required for Full Day Present status">
                    <InputNumber min={1} max={24} step={0.5} style={{ width: '100%' }} suffix="hrs" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="half_day_hours" label="Half Day Minimum Hours (HD)" rules={[{ required: true }]} tooltip="Hours required for Half Day. Below this is marked as Absent">
                    <InputNumber min={0.5} max={12} step={0.5} style={{ width: '100%' }} suffix="hrs" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="overtime_threshold_hours" label="Overtime Threshold Hours (OT)" rules={[{ required: true }]} tooltip="Hours beyond which overtime calculation begins">
                    <InputNumber min={1} max={24} step={0.5} style={{ width: '100%' }} suffix="hrs" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {editingCandSettingId && <Tag color="gold">Editing Existing Rule ID: {editingCandSettingId}</Tag>}
                Target: <b>{candScheduleType === 'DATE' ? `Date: ${candSpecificDate?.format('DD/MM/YYYY')}` : `Days: ${selectedCandDays.join(', ')}`}</b> • Comb: <b>{candCombination}</b>
              </Text>
              <Space>
                {editingCandSettingId && (
                  <Button size="middle" onClick={() => setEditingCandSettingId(null)}>
                    Cancel Edit
                  </Button>
                )}
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() => handleSave('CANDIDATE')}
                  loading={saving}
                  size="middle"
                >
                  Save Candidate Rule
                </Button>
              </Space>
            </div>
          </Form>

          <Divider style={{ margin: '12px 0' }} />

          {/* Candidate Rules Table */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 'bold', color: token.colorText }}>
                Active Configured Candidate Shift Rules ({candidateRules.length}):
              </span>
            </div>
            <Table
              columns={candRuleColumns}
              dataSource={candidateRules}
              rowKey={(r) => r.setting_id || `${r.person_type}_${r.day_type}_${r.combination}_${r.specific_date}`}
              size="small"
              pagination={false}
              bordered
              scroll={{ x: 'max-content' }}
            />
          </div>
        </div>
      )
    }
  ];

  if (!isStudio) {
    tabItems.push({
      key: 'STAFF',
      label: (
        <span>
          <UserOutlined /> Staff Shift Timings (Individual Staff, Manual Days & Date-Wise)
        </span>
      ),
      children: (
        <div>
          {/* Header Controls for Staff */}
          <Card
            size="small"
            style={{
              marginBottom: 16,
              background: token.colorInfoBg || 'rgba(24, 144, 255, 0.06)',
              borderColor: token.colorInfoBorder || 'rgba(24, 144, 255, 0.3)'
            }}
          >
            <Row gutter={[16, 12]} align="middle">
              <Col xs={24} md={10}>
                <div style={{ fontSize: 12, fontWeight: 'bold', color: token.colorPrimary, marginBottom: 6 }}>
                  <UserOutlined /> Staff Target Selection:
                </div>
                <Radio.Group
                  value={staffTargetType}
                  onChange={(e) => {
                    setStaffTargetType(e.target.value);
                    setEditingStaffSettingId(null);
                  }}
                  buttonStyle="solid"
                  size="small"
                >
                  <Radio.Button value="ALL">👥 All Staff Members</Radio.Button>
                  <Radio.Button value="INDIVIDUAL">👤 Individual Staff Member</Radio.Button>
                </Radio.Group>
              </Col>

              {staffTargetType === 'INDIVIDUAL' && (
                <Col xs={24} md={8}>
                  <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 6, color: token.colorPurple || '#9333ea' }}>
                    Select Specific Staff Member:
                  </div>
                  <Select
                    placeholder="Choose staff member..."
                    value={selectedStaffId}
                    onChange={(v) => {
                      setSelectedStaffId(v);
                      setEditingStaffSettingId(null);
                    }}
                    style={{ width: '100%' }}
                    size="small"
                    showSearch
                    optionFilterProp="children"
                  >
                    {staffList.map(s => (
                      <Option key={s.staff_id} value={s.staff_id}>
                        {s.staff_name} ({s.staff_code || `ID: ${s.staff_id}`})
                      </Option>
                    ))}
                  </Select>
                </Col>
              )}

              <Col xs={24} md={staffTargetType === 'INDIVIDUAL' ? 6 : 14}>
                <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 6, color: token.colorCyan || token.colorPrimary }}>
                  <CalendarOutlined /> Schedule Configuration Mode:
                </div>
                <Radio.Group
                  value={staffScheduleType}
                  onChange={(e) => {
                    setStaffScheduleType(e.target.value);
                    setEditingStaffSettingId(null);
                  }}
                  buttonStyle="solid"
                  size="small"
                >
                  <Radio.Button value="DAYS">📅 Regular Days</Radio.Button>
                  <Radio.Button value="DATE">🗓️ Date-Wise Override</Radio.Button>
                </Radio.Group>
              </Col>
            </Row>

            <Divider style={{ margin: '10px 0' }} />

            {/* Scope Details (Days vs Specific Date) for Staff */}
            {staffScheduleType === 'DAYS' ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: '600', color: token.colorTextSecondary }}>
                    Select Days for Staff Shift:
                  </div>
                  <Space size="small">
                    <Button size="small" onClick={() => handleApplyStaffPreset('MON_SAT')}>
                      Mon - Sat (Standard)
                    </Button>
                    <Button size="small" onClick={() => handleApplyStaffPreset('WEEKDAY')}>
                      Mon - Fri
                    </Button>
                    <Button size="small" onClick={() => handleApplyStaffPreset('SATURDAY')}>
                      Saturday
                    </Button>
                    <Button size="small" onClick={() => handleApplyStaffPreset('ALL')}>
                      All 7 Days
                    </Button>
                  </Space>
                </div>
                <Checkbox.Group
                  options={DAY_OPTIONS}
                  value={selectedStaffDays}
                  onChange={(days) => {
                    setSelectedStaffDays(days);
                    setEditingStaffSettingId(null);
                  }}
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
                />
              </div>
            ) : (
              <Row gutter={12} align="middle">
                <Col xs={24} sm={12} md={8}>
                  <div style={{ fontSize: 12, fontWeight: '600', color: token.colorTextSecondary, marginBottom: 4 }}>
                    Select Specific Calendar Date:
                  </div>
                  <DatePicker
                    value={staffSpecificDate}
                    onChange={(d) => {
                      setStaffSpecificDate(d);
                      setEditingStaffSettingId(null);
                    }}
                    format="DD/MM/YYYY"
                    style={{ width: '100%' }}
                    allowClear={false}
                  />
                </Col>
                <Col xs={24} sm={12} md={16}>
                  <Alert
                    message="Date-Wise Staff Shift Override"
                    description="Applies special login/logout windows & duration hours for staff on this specific calendar date."
                    type="info"
                    showIcon
                    style={{ padding: '4px 10px', fontSize: 12 }}
                  />
                </Col>
              </Row>
            )}
          </Card>

          {/* Form */}
          <Form form={staffForm} layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Card size="small" title={<span style={{ color: token.colorPrimary }}><ClockCircleOutlined /> Check-In / Login Window</span>} style={{ marginBottom: 16 }}>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item name="login_from" label="Login From (Shift Start)" rules={[{ required: true }]}>
                        <TimePicker format="HH:mm:ss" style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="login_to" label="Login To (Late Cutoff)" rules={[{ required: true }]} tooltip="Punches after this time will be marked as Late (L)">
                        <TimePicker format="HH:mm:ss" style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Col>

              <Col span={12}>
                <Card size="small" title={<span style={{ color: token.colorPurple || '#9333ea' }}><ClockCircleOutlined /> Check-Out / Logout Window</span>} style={{ marginBottom: 16 }}>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item name="logout_from" label="Logout From (Early Leave Cutoff)" rules={[{ required: true }]} tooltip="Check-out before this time is considered early departure">
                        <TimePicker format="HH:mm:ss" style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="logout_to" label="Logout To (Standard Shift End)" rules={[{ required: true }]} tooltip="Punches after this time qualify for Overtime (OT)">
                        <TimePicker format="HH:mm:ss" style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>

            <Card size="small" title={<span style={{ color: token.colorSuccess }}><ThunderboltOutlined /> Duration & Threshold Hours</span>} style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="full_day_hours" label="Full Day Standard Hours (P)" rules={[{ required: true }]} tooltip="Minimum hours required for Full Day Present status">
                    <InputNumber min={1} max={24} step={0.5} style={{ width: '100%' }} suffix="hrs" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="half_day_hours" label="Half Day Minimum Hours (HD)" rules={[{ required: true }]} tooltip="Hours required for Half Day. Below this is marked as Absent">
                    <InputNumber min={0.5} max={12} step={0.5} style={{ width: '100%' }} suffix="hrs" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="overtime_threshold_hours" label="Overtime Threshold Hours (OT)" rules={[{ required: true }]} tooltip="Hours beyond which overtime calculation begins">
                    <InputNumber min={1} max={24} step={0.5} style={{ width: '100%' }} suffix="hrs" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {editingStaffSettingId && <Tag color="gold">Editing Existing Rule ID: {editingStaffSettingId}</Tag>}
                Target: <b>{staffTargetType === 'INDIVIDUAL' && selectedStaffId ? `Staff ID: ${selectedStaffId}` : 'All Staff'}</b> • Schedule: <b>{staffScheduleType === 'DATE' ? `Date: ${staffSpecificDate?.format('DD/MM/YYYY')}` : `Days: ${selectedStaffDays.join(', ')}`}</b>
              </Text>
              <Space>
                {editingStaffSettingId && (
                  <Button size="middle" onClick={() => setEditingStaffSettingId(null)}>
                    Cancel Edit
                  </Button>
                )}
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() => handleSave('STAFF')}
                  loading={saving}
                  size="middle"
                >
                  Save Staff Timings
                </Button>
              </Space>
            </div>
          </Form>

          <Divider style={{ margin: '12px 0' }} />

          {/* Staff Rules Table */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 'bold', color: token.colorText }}>
                Active Configured Staff Shift Rules ({staffRules.length}):
              </span>
            </div>
            <Table
              columns={staffRuleColumns}
              dataSource={staffRules}
              rowKey={(r) => r.setting_id || `${r.person_type}_${r.day_type}_${r.staff_id}_${r.specific_date}`}
              size="small"
              pagination={false}
              bordered
              scroll={{ x: 'max-content' }}
            />
          </div>
        </div>
      )
    });
  }

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ClockCircleOutlined style={{ color: token.colorPrimary, fontSize: 18 }} />
          <span>{isStudio ? 'Candidate Shift Timings Configuration' : 'Shift Timings & Timing Windows Configuration'}</span>
        </div>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      width={960}
      destroyOnClose
    >
      <Tabs
        activeKey={activePersonType}
        onChange={setActivePersonType}
        items={tabItems}
      />
    </Modal>
  );
}
